import { useS3FilesManagement } from "@app/hooks/createFileHook";
import { ExtensionTag } from "@app/shared/components/ExtensionTag";
import { type NewS3File } from "@app/usecases/FileUseCases";
import { getFileFormatCategoryFromMimeType } from "@app/usecases/VersionUseCases/utils";
import { Icon } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import DropZone from "@design-system/Inputs/DropZone";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import { useState, type FC } from "react";
import {
    useCreateQuestionsFromFile,
    useDownloadQuestionsCreationTemplate,
} from "../../data";
import { useCreateQuestionsFromUploadModalTranslations } from "./i18n";

const acceptedFileTypes = [
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel.sheet.macroEnabled.12",
];

export const CreateQuestionnaireFromUploadModal: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
}> = ({ open, onOpenChange }) => {
    const { t, keys } = useCreateQuestionsFromUploadModalTranslations();
    const { uploadFiletoS3 } = useS3FilesManagement();
    const [numberOfQuestionsCreated, setNumberOfQuestionsCreated] = useState(0);
    const [errors, setErrors] = useState<string[] | null>(null);
    const [uploadedFile, setUploadedFile] = useState<NewS3File | null>(null);

    const resetStates = () => {
        setNumberOfQuestionsCreated(0);
        setErrors(null);
        setUploadedFile(null);
    };

    const { downloadQuestionsCreationTemplate } =
        useDownloadQuestionsCreationTemplate();

    const handleDownload = () => {
        downloadQuestionsCreationTemplate();
    };

    const {
        createQuestionsFromFileAsync,
        isPending: isImportingFromFilePending,
    } = useCreateQuestionsFromFile();

    const handleFileDrop = async (file: File) => {
        const uploadedFile = await uploadFiletoS3(file);
        setUploadedFile(uploadedFile);

        const { createSupplierQuestionsFromFile } =
            await createQuestionsFromFileAsync({
                s3File: uploadedFile,
            });

        setNumberOfQuestionsCreated(
            createSupplierQuestionsFromFile?.data?.createdQuestionsCount ?? 0,
        );
        setErrors(createSupplierQuestionsFromFile?.data?.errors ?? null);
    };

    const handleOpenChange = (open: boolean) => {
        resetStates();
        onOpenChange(open);
    };

    return (
        <Modal open={open} onOpenChange={handleOpenChange}>
            <Modal.Overlay scrollable>
                <Modal.Content>
                    <Modal.Header withClose>
                        <FlexRow gap="4">
                            <FlexCol gap="2">
                                <Modal.Title>
                                    {t(keys.upload_file_modal_title)}
                                </Modal.Title>
                            </FlexCol>
                        </FlexRow>
                    </Modal.Header>
                    <Modal.Body>
                        <FlexCol gap="4">
                            <Text variant="body-sm-bold">
                                {t(keys.download_template_step_title)}
                            </Text>
                            <Text variant="body-sm">
                                {t(keys.download_template_step_description)}
                            </Text>

                            <Button
                                variant="outlined"
                                size="sm"
                                onClick={handleDownload}
                                className="max-w-min"
                            >
                                <Button.Icon name="arrowDownload" />
                                {t(keys.download_template_button_label)}
                            </Button>

                            <Text variant="body-sm-bold">
                                {t(keys.fill_template_step_title)}
                            </Text>
                            <Text variant="body-sm">
                                {t(keys.fill_template_step_description)}
                            </Text>

                            <Text variant="body-sm-bold">
                                {t(keys.upload_file_step_title)}
                            </Text>

                            {isImportingFromFilePending && (
                                <FlexCol
                                    alignItems="center"
                                    justifyContent="center"
                                    gap="2"
                                    className="min-h-[200px]"
                                >
                                    <Text
                                        className="animate-pulse"
                                        color="brand"
                                    >
                                        {t(keys.analyzing_file)}
                                    </Text>
                                    <div className="loader" />
                                </FlexCol>
                            )}
                            {!isImportingFromFilePending &&
                                numberOfQuestionsCreated === 0 && (
                                    <FlexCol gap="2">
                                        <DropZone
                                            accept={acceptedFileTypes}
                                            buttonText={t(
                                                keys.upload_file_modal_button_label,
                                            )}
                                            multiple={false}
                                            onChange={handleFileDrop}
                                            size={"md"}
                                            loading={isImportingFromFilePending}
                                            className="w-full h-full"
                                        >
                                            {t(
                                                keys.upload_file_modal_placeholder,
                                            )}
                                        </DropZone>
                                        <Text variant="body-sm-bold">
                                            {t(keys.reminder_step_title)}
                                        </Text>
                                        <Text variant="body-sm">
                                            {t(keys.reminder_step_description)}
                                        </Text>
                                    </FlexCol>
                                )}

                            {(errors || numberOfQuestionsCreated > 0) && (
                                <Box
                                    br="lg"
                                    p="2.5"
                                    className={
                                        (errors?.length ?? 0) > 0
                                            ? "bg-error-100 border border-error-700"
                                            : "bg-green-50 border border-green-700"
                                    }
                                >
                                    <FlexCol gap="4">
                                        {uploadedFile && (
                                            <FlexRow
                                                alignItems="center"
                                                gap="3"
                                                alignSelf="start"
                                                key={uploadedFile.signedUrl}
                                            >
                                                <ExtensionTag
                                                    name={getFileFormatCategoryFromMimeType(
                                                        uploadedFile.mimeType,
                                                    )}
                                                />
                                                <a
                                                    href={
                                                        uploadedFile.signedUrl
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-inherit cursor-pointer"
                                                >
                                                    <Text
                                                        variant="body-bold"
                                                        className="text-primary hover:text-beavrGreen"
                                                    >
                                                        {uploadedFile.name}
                                                    </Text>
                                                </a>
                                            </FlexRow>
                                        )}
                                        {numberOfQuestionsCreated > 0 && (
                                            <FlexRow gap="2">
                                                <Icon name="check" />
                                                <Text>
                                                    {t(keys.questions_created, {
                                                        count: numberOfQuestionsCreated,
                                                    })}
                                                </Text>
                                            </FlexRow>
                                        )}
                                        {errors && errors.length > 0 && (
                                            <FlexCol gap="2">
                                                <FlexRow gap="2">
                                                    <Icon name="warning" />
                                                    <Text>
                                                        {t(
                                                            keys.questions_error,
                                                        )}
                                                    </Text>
                                                </FlexRow>
                                                <ul className="list-inside text-sm text-secondary list-disc">
                                                    {errors.map((error) => (
                                                        <li key={error}>
                                                            {error}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </FlexCol>
                                        )}
                                    </FlexCol>
                                </Box>
                            )}
                        </FlexCol>
                    </Modal.Body>
                    <Modal.Footer>
                        <Modal.ConfirmCTA>{t(keys.close)}</Modal.ConfirmCTA>
                    </Modal.Footer>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};
